/**
 * @file NeedsActionView.tsx
 * @description
 * Enhanced subcomponent for the NEEDS_ACTION status. 
 * Displays IRB feedback from review_data, an "Edit Submission" button,
 * and an explanation of what to do next.
 *
 * Key Features:
 * 1. Shows IRB feedback if submission.review_data is present.
 * 2. Provides an "Edit Submission" button to navigate user to the existing 
 *    SubmissionForm in edit mode (with submissionId).
 * 3. Mentions that once they are done editing, they can resubmit.
 *
 * @dependencies
 * - React
 * - React Router: navigate
 *
 * @notes
 * - Part of Step 4: NEEDS_ACTION features. 
 * - Actual logic to highlight fields requiring changes or re-upload documents 
 *   is done in SubmissionForm & FormSubmissionFields. This view simply 
 *   invites the user to do so and displays any IRB feedback.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Disclosure } from '@headlessui/react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { useAuth } from '../../hooks/useAuth';
import { Document } from '../../lib/supabaseClient';
import FeedbackAndResponses from '../FeedbackAndResponses';
import { SubmissionRow } from '../../types/submission';

interface NeedsActionViewProps {
  submission: SubmissionRow;
}

// Helper function to determine the bucket name based on document type
const getDocumentBucket = (docType: string): string => {
  return ['submission_form_markdown', 'submission_form_pdf'].includes(docType)
    ? 'tempus-created-docs'
    : 'user-uploaded-docs';
};

/**
 * Renders the "Needs Action" UI:
 * - Displays IRB feedback from review_data
 * - Provides an edit button to let user fix or re-upload documents
 */
const NeedsActionView: React.FC<NeedsActionViewProps> = ({ submission }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submissionFormPdfs, setSubmissionFormPdfs] = useState<Document[]>([]);
  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  
  const supabase = useSupabase();

  const handleEditSubmission = () => {
    // Navigate to /new-submission?edit=<submissionId>
    navigate(`/edit-submission?edit=${submission.id}`);
  };

  const handleCompletePayment = () => {
    // Send the user to the placeholder Billing page
    navigate(`/billing?submissionId=${submission.id}`);
  };

  // Fetch submission form PDFs
  useEffect(() => {
    const fetchSubmissionFormPdfs = async () => {
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('submission_id', submission.id)
          .eq('type', 'submission_form_pdf')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching submission form PDFs:', error);
          return;
        }

        setSubmissionFormPdfs((data || []) as Document[]);
      } catch (error) {
        console.error('Failed to fetch submission form PDFs:', error);
      }
    };

    fetchSubmissionFormPdfs();
  }, [submission.id, supabase]);

  // Generate signed URLs for PDFs
  useEffect(() => {
    const generateSignedUrls = async () => {
      if (!submissionFormPdfs.length || !user) return;
      
      const urls: Record<string, string> = {};
      
      for (const doc of submissionFormPdfs) {
        // Determine which bucket to use based on document type
        const bucketName = getDocumentBucket(doc.type || '');
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .createSignedUrl(doc.file_path, 3600); // 1 hour expiry
        
        if (data && !error) {
          urls[doc.id] = data.signedUrl;
        } else {
          console.error(`Error generating signed URL from ${bucketName}:`, error);
        }
      }
      
      setDocumentUrls(urls);
    };
    
    generateSignedUrls();
  }, [submissionFormPdfs, supabase, user]);

  return (
    <div>
      <h3 className="text-xl font-bold mt-3 mb-3 text-[#F55D3E]">Edits Required Before IRB Review Can Continue</h3>
      <p className="text-gray-700 mb-3">
        The IRB has requested edits for your submission that must be addressed before the review process can proceed. Please review the IRB reviewer's feedback 
        below, then click the "Edit Submission" below to make changes and record your responses to each feedback item. 
      </p>

      {/* Use the new FeedbackAndResponses component */}
      <div className="bg-white rounded-md border border-gray-200 p-4 mb-6">
        {submission.review_data && submission.review_data.review_cycles && submission.review_data.review_cycles.length > 0 ? (
          <FeedbackAndResponses 
            submissionId={submission.id} 
            mode="view"
          />
        ) : (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 text-yellow-800">
            <p className="font-semibold">No IRB feedback details found.</p>
            <p>You may still edit your submission if needed.</p>
          </div>
        )}
      </div>

      <div className="flex space-x-4 mb-6">
        <button
          onClick={handleEditSubmission}
          className="inline-flex items-center justify-center bg-[#F55D3E] text-white rounded-md px-4 py-2 font-semibold hover:bg-[#d94a30] focus:outline-none focus:ring-2 focus:ring-[#F55D3E]"
        >
          Edit Submission To Address Feedback
        </button>
      </div>

      {/* Data from Previous Review Cycles section */}
      {submission.review_data && 
       submission.review_data.review_cycles && 
       submission.review_data.review_cycles.length > 1 && (
        <Disclosure as="div" className="mt-4 mb-6">
          {({ open }) => (
            <>
              <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
                <span>Feedback & responses from previous review cycles</span>
                <svg
                  className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Disclosure.Button>
              <Disclosure.Panel className="px-4 pt-4 pb-2">
                {submission.review_data?.review_cycles
                  .slice(0, -1) // All cycles except the latest one
                  .map((cycle, index) => (
                    <div key={`previous-cycle-${index}`} className="mb-6 border-b pb-6 last:border-b-0">
                      <h4 className="font-semibold text-gray-700 mb-2">
                        Review Cycle {cycle.cycle} 
                      </h4>
                      <FeedbackAndResponses
                        submissionId={submission.id}
                        cycleNumber={cycle.cycle}
                        mode="view"
                      />
                    </div>
                  ))}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      )}

      {/* Previous submission form PDFs section */}
      <Disclosure as="div" className="mt-4 mb-6">
        {({ open }) => (
          <>
            <Disclosure.Button className="flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-blue-900 bg-blue-100 rounded-lg hover:bg-blue-200 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75">
              <span>Previous submission form PDFs</span>
              <svg
                className={`w-5 h-5 text-blue-500 ${open ? 'transform rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-500">
              {submissionFormPdfs.length === 0 ? (
                <p className="text-gray-500 italic">No submission form PDFs found</p>
              ) : (
                <ul className="space-y-2">
                  {submissionFormPdfs.map((doc) => (
                    <li key={doc.id} className="flex items-center">
                      <svg 
                        className="w-5 h-5 text-gray-600 mr-2" 
                        fill="currentColor" 
                        viewBox="0 0 20 20"
                      >
                        <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                      </svg>
                      {documentUrls[doc.id] ? (
                        <button 
                          onClick={() => {
                            // Force download by converting to blob
                            fetch(documentUrls[doc.id])
                              .then(response => response.blob())
                              .then(blob => {
                                // Create blob URL and trigger download
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = doc.filename;
                                document.body.appendChild(link);
                                link.click();
                                // Clean up
                                window.URL.revokeObjectURL(blobUrl);
                                document.body.removeChild(link);
                              })
                              .catch(err => {
                                console.error("Download failed:", err);
                                alert("Download failed. Please try again.");
                              });
                          }}
                          className="text-[#F55D3E] hover:underline flex items-center"
                        >
                          {doc.filename}
                        </button>
                      ) : (
                        <span className="flex items-center text-gray-400">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Loading file...
                        </span>
                      )}
                      <span className="text-xs text-gray-500 ml-2">
                        (Uploaded: {new Date(doc.uploaded_at).toLocaleDateString()})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      
      {/* Payment Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-lg mb-2">Payment Status</h3>
        {submission.payment_completed ? (
          <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded">
            Payment is complete! 
          </div>
        ) : (
          <div className="p-3 text-[#B83EF5] border border-yellow-200 rounded flex flex-col space-y-3">
            <p>
              Payment has <strong>not</strong> been completed. IRB decisions will be gated until
              payment is received.
            </p>
            <button
              onClick={handleCompletePayment}
              className="px-4 py-2 bg-[#B83EF5] text-white rounded hover:bg-[#9c32c7]-50 self-start"
            >
              Complete Payment
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NeedsActionView;
